import type {
  AgentDiagnoseEvent,
  AgentDiagnoseRequest,
  AgentFocus,
  AgentSessionSummary,
  AgentSummary,
  AgentTurn,
  DiagnoseTransport,
} from "./types";

/**
 * 진단 에이전트 상류의 클라이언트입니다. (ADR 0043 결정 3 · ADR 0045)
 *
 * k8sdiag 클라이언트와 **같은 파일에 두지 않습니다.** 두 상류는 이벤트도 세션도
 * 다르고, 한 모듈에 섞으면 어느 쪽 계약인지 호출부가 알 수 없게 됩니다.
 */

/** 대시보드 BFF의 에이전트 경로. 호스트가 다른 경로를 쓰면 transport를 넘깁니다. */
export const defaultAgentTransport: DiagnoseTransport = (path, init) =>
  fetch(`/api/v1/agents${path}`, { credentials: "same-origin", ...init });

const EVENTS = new Set(["context_used", "message_delta", "tool_progress", "approval_required", "error"]);
/** 한 스트림이 실어 나를 수 있는 상한입니다. 서버도 같은 자리에 상한을 겁니다. */
const MAX_STREAM_BYTES = 1 << 20;

/**
 * 스트림이 계약을 벗어났습니다.
 *
 * 별도 타입인 이유는 호출부가 "연결이 끊겼다"와 "약속과 다른 것이 왔다"를 다르게
 * 다뤄야 하기 때문입니다 — 후자는 재시도가 아니라 보고할 사실입니다.
 */
export class AgentStreamError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "AgentStreamError";
  }
}

/**
 * 진단에 쓸 수 있는 에이전트를 묻습니다.
 *
 * 실패·미설정(503)·401은 전부 **빈 목록**입니다 — 셋 다 "엔진 선택을 그리지
 * 않는다"는 같은 결론이고, 고를 수 없는 컨트롤을 비활성으로 남기지 않습니다.
 */
export async function fetchAgents(
  transport: DiagnoseTransport,
  signal: AbortSignal,
): Promise<AgentSummary[]> {
  try {
    const res = await transport("", { headers: { accept: "application/json" }, signal });
    if (!res.ok) {
      await res.body?.cancel();
      return [];
    }
    const value = (await res.json()) as { agents?: AgentSummary[] };
    if (!Array.isArray(value?.agents)) return [];
    return value.agents.filter((agent) => typeof agent?.id === "string" && agent.id !== "");
  } catch {
    return [];
  }
}

export async function fetchAgentSessions(
  transport: DiagnoseTransport,
  agentId: string,
  signal: AbortSignal,
): Promise<AgentSessionSummary[]> {
  try {
    const res = await transport(`/${encodeURIComponent(agentId)}/sessions`, {
      headers: { accept: "application/json" },
      signal,
    });
    if (!res.ok) {
      await res.body?.cancel();
      return [];
    }
    const value = (await res.json()) as { sessions?: AgentSessionSummary[] };
    return Array.isArray(value?.sessions) ? value.sessions : [];
  } catch {
    return [];
  }
}

export async function fetchAgentSessionTurns(
  transport: DiagnoseTransport,
  agentId: string,
  sessionId: string,
): Promise<AgentTurn[]> {
  try {
    const res = await transport(`/${encodeURIComponent(agentId)}/sessions/${encodeURIComponent(sessionId)}`, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      await res.body?.cancel();
      return [];
    }
    const value = (await res.json()) as { turns?: AgentTurn[] };
    return Array.isArray(value?.turns) ? value.turns : [];
  } catch {
    return [];
  }
}

/** 좌표를 에이전트가 받는 형태로 옮깁니다. 공유가 꺼져 있으면 아무것도 없습니다. */
export function agentFocus(focus: AgentFocus | null | undefined): AgentFocus | undefined {
  if (!focus) return undefined;
  return {
    kind: focus.kind,
    ...(focus.cluster_id ? { cluster_id: focus.cluster_id } : {}),
    ...(focus.namespace ? { namespace: focus.namespace } : {}),
    ...(focus.workload_kind ? { workload_kind: focus.workload_kind } : {}),
    ...(focus.name ? { name: focus.name } : {}),
  };
}

/**
 * 질문 하나를 에이전트에게 SSE로 묻습니다.
 *
 * 반환값은 이어지는 질문에 되돌려 줄 **에이전트 대화** 식별자입니다 — k8sdiag
 * 세션과 다른 값이고, 두 목록은 섞이지 않습니다.
 */
export async function streamAgentDiagnose(
  transport: DiagnoseTransport,
  agentId: string,
  body: AgentDiagnoseRequest,
  onEvent: (event: AgentDiagnoseEvent) => void,
  signal: AbortSignal,
): Promise<string> {
  const res = await transport(`/${encodeURIComponent(agentId)}/diagnose/stream`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    await res.body?.cancel();
    throw new AgentStreamError(`agent_rejected_${res.status}`);
  }
  const sessionId = res.headers.get("X-Session-Id") ?? "";
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let seen = 0;
  /* 서버는 상류의 `[DONE]`을 본 뒤에만 `done`을 냅니다. 그 표식 없이 본문이
     끝난 것은 **잘린 답**이고, 조용히 성공으로 돌려주면 화면이 완결된 답을
     보여 줍니다. */
  let completed = false;
  /* 취소를 존중하지 않는 전송도 있습니다 — 그때 `read()`는 영원히 기다립니다.
     신호를 직접 듣고 본문을 끊어야 상류 연결이 실제로 닫힙니다. */
  const cancelOnAbort = () => {
    void reader.cancel().catch(() => undefined);
  };
  if (signal.aborted) cancelOnAbort();
  else signal.addEventListener("abort", cancelOnAbort, { once: true });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) {
        seen += value.byteLength;
        if (seen > MAX_STREAM_BYTES) throw new AgentStreamError("agent_stream_too_large");
        buffer += decoder.decode(value, { stream: true });
      }
      let end = buffer.indexOf("\n\n");
      while (end >= 0) {
        if (emit(buffer.slice(0, end), onEvent)) completed = true;
        buffer = buffer.slice(end + 2);
        end = buffer.indexOf("\n\n");
      }
      if (done) {
        if (emit(buffer, onEvent)) completed = true;
        break;
      }
      if (completed) break;
    }
    if (!completed) throw new AgentStreamError("agent_stream_truncated");
  } catch (error) {
    /* 비정상 종료입니다 — 잠금만 풀면 상류 연결은 살아 있고, 그 바이트는 아무도
       읽지 않는 채로 계속 흐릅니다. 취소를 먼저 보냅니다. */
    await reader.cancel().catch(() => undefined);
    throw error;
  } finally {
    signal.removeEventListener("abort", cancelOnAbort);
    reader.releaseLock();
  }
  return sessionId;
}

/** 상류가 준 라벨 하나입니다 — 비지 않고, 유계이며, 제어문자가 없습니다. */
function label(value: unknown, max = 128): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= max && !/[\u0000-\u001f\u007f]/.test(value);
}

function reject(): never {
  throw new AgentStreamError("agent_stream_malformed");
}

/**
 * 파싱된 data를 **이 계약의 이벤트**로 좁힙니다.
 *
 * JSON으로 읽혔다는 것은 형태가 맞다는 뜻이 아닙니다. `{"delta":undefined}`가
 * 그대로 통과하면 화면은 답변 끝에 `undefined`를 이어 붙이고, 그것이 모델이 쓴
 * 글자인지 우리 버그인지 운영자는 구별할 수 없습니다. 필요한 필드만 봅니다 —
 * 스키마 계층을 새로 만들지 않고, 모르는 필드는 **버립니다**(옮기지 않습니다).
 */
function typedEvent(event: string, value: unknown): AgentDiagnoseEvent {
  if (typeof value !== "object" || value === null || Array.isArray(value)) reject();
  const data = value as Record<string, unknown>;
  switch (event) {
    case "message_delta": {
      if (typeof data.delta !== "string") reject();
      return { event: "message_delta", data: { delta: data.delta as string } };
    }
    case "tool_progress": {
      const { id, name, phase, kind, status, elapsed_ms: elapsed } = data;
      /* 병렬 실행을 구별하는 것은 id입니다. phase는 시작과 끝 둘뿐이고, 그 밖의
         값은 상류가 말하지 않은 단계라 화면이 뜻을 지어내게 됩니다. */
      if (!label(id) || !label(name) || (phase !== "start" && phase !== "end")) reject();
      if (kind !== undefined && !label(kind, 32)) reject();
      if (status !== undefined && !label(status, 32)) reject();
      if (elapsed !== undefined && (typeof elapsed !== "number" || !Number.isFinite(elapsed))) reject();
      return {
        event: "tool_progress",
        data: {
          id: id as string,
          name: name as string,
          phase: phase as string,
          ...(kind === undefined ? {} : { kind: kind as string }),
          ...(status === undefined ? {} : { status: status as string }),
          ...(elapsed === undefined ? {} : { elapsed_ms: elapsed as number }),
        },
      };
    }
    case "approval_required": {
      /* 지원한다고 말하는 승인 요청은 이 패널의 계약이 아닙니다. */
      if (data.supported !== false) reject();
      return { event: "approval_required", data: { supported: false } };
    }
    case "error": {
      if (!label(data.error, 64)) reject();
      return { event: "error", data: { error: data.error as string } };
    }
    case "context_used": {
      if (data.source !== "agent") reject();
      const focus = data.focus;
      if (focus === null) return { event: "context_used", data: { focus: null, source: "agent" } };
      if (typeof focus !== "object" || focus === null || Array.isArray(focus)) reject();
      const coords = focus as Record<string, unknown>;
      const kind = coords.kind;
      if (kind !== "cluster" && kind !== "namespace" && kind !== "workload" && kind !== "pod") reject();
      for (const key of ["cluster_id", "namespace", "workload_kind", "name"]) {
        if (coords[key] !== undefined && !label(coords[key], 253)) reject();
      }
      return {
        event: "context_used",
        data: {
          focus: {
            kind,
            ...(coords.cluster_id === undefined ? {} : { cluster_id: coords.cluster_id as string }),
            ...(coords.namespace === undefined ? {} : { namespace: coords.namespace as string }),
            ...(coords.workload_kind === undefined ? {} : { workload_kind: coords.workload_kind as string }),
            ...(coords.name === undefined ? {} : { name: coords.name as string }),
          },
          source: "agent",
        },
      };
    }
    default:
      return reject();
  }
}

/**
 * 프레임 하나를 이벤트로 옮깁니다. 완결 표식(`done`)을 보면 참을 돌려줍니다.
 *
 * 계약을 벗어난 프레임은 **버리지 않고 거절합니다.** 조용히 버리면 답의 일부가
 * 사라진 스트림이 정상 종료처럼 보이고, 화면은 그 차이를 말할 수 없습니다.
 * `:`로 시작하는 주석(keepalive)과 data 없는 프레임은 계약 안이라 그대로 넘깁니다.
 */
function emit(frame: string, onEvent: (event: AgentDiagnoseEvent) => void): boolean {
  let event = "";
  const data: string[] = [];
  let sawField = false;
  for (const line of frame.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    sawField = true;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    else reject();
  }
  if (!sawField) return false;
  if (event !== "done" && !EVENTS.has(event)) reject();
  if (!data.length) reject();
  let parsed: unknown;
  try {
    parsed = JSON.parse(data.join("\n"));
  } catch {
    return reject();
  }
  if (event === "done") {
    /* 완결 표식은 빈 객체 하나입니다. 다른 것이 실려 오면 우리가 아는 그
       표식이 아니고, 그것을 완결로 읽으면 잘린 답이 성공이 됩니다. */
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed) || Object.keys(parsed).length > 0) {
      reject();
    }
    return true;
  }
  /* onEvent가 던지면 그대로 올려 보냅니다 — 삼키면 화면이 갱신되지 않은 채로
     스트림만 성공합니다. */
  const typed = typedEvent(event, parsed);
  onEvent(typed);
  if (typed.event === "error") {
    /* `error`는 **종결**입니다. 서버는 그 뒤에 `done`을 내지 않습니다. 여기서
       기다리면 본문이 끝나는 순간 `agent_stream_truncated`가 되어, 상류가 말한
       진짜 사유(403 거절·시간 초과·형식 불일치)가 지워집니다. 사유를 그대로
       실어 던지고, 호출부가 연결을 끊습니다. (ADR 0043 결과 — 패널은 실패
       모드를 구분해 보여 줘야 합니다) */
    throw new AgentStreamError(typed.data.error);
  }
  return false;
}
